import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Play, Pause, Volume2, VolumeX, Loader2, Film, RefreshCw, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProcessVideo = () => {
  // Pre-generated video URL - using streaming endpoint
  const preGeneratedVideoUrl = `${BACKEND_URL}/api/stream-video/17fffd4d-81a9-4cbb-8416-0ffa0d60ab8f`;
  
  const [videoUrl, setVideoUrl] = useState(null);
  const [hasPreGeneratedVideo, setHasPreGeneratedVideo] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [error, setError] = useState(null);
  const [showBalanceWarning, setShowBalanceWarning] = useState(false);
  const videoRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Load pre-generated video on mount
  useEffect(() => {
    setVideoUrl(preGeneratedVideoUrl);
  }, []);

  const videoPrompt = `A cinematic documentary-style video showing the complete journey of premium Basmati rice production in India: 
  Starting with lush green paddy fields in Haryana at golden hour with farmers carefully harvesting rice, 
  then transitioning to a modern rice mill where workers operate machinery for cleaning and processing, 
  followed by quality control lab technicians testing rice samples under microscopes, 
  then showcasing packaging lines where rice is carefully packed into bags, 
  and finally shipping containers being loaded onto trucks for global export. 
  The video should have warm, professional lighting and smooth transitions between scenes.`;

  const generateVideo = async () => {
    setIsGenerating(true);
    setError(null);
    setShowBalanceWarning(false);
    setGenerationStatus('Starting video generation...');

    try {
      const response = await axios.post(`${API}/generate-video`, {
        prompt: videoPrompt,
        duration: 12
      });

      const videoId = response.data.id;
      setGenerationStatus('Video is being generated. This may take 5-10 minutes...');

      // Poll for status
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusResponse = await axios.get(`${API}/video-status/${videoId}`);
          const status = statusResponse.data;

          if (status.status === 'completed') {
            clearInterval(pollIntervalRef.current);
            // Construct full URL for the video
            const fullVideoUrl = `${BACKEND_URL}${status.video_url}`;
            setVideoUrl(fullVideoUrl);
            setIsGenerating(false);
            setGenerationStatus('Video ready!');
          } else if (status.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            setIsGenerating(false);
            if (status.message && status.message.includes('insufficient_balance')) {
              setShowBalanceWarning(true);
              setError('Video credits depleted. Please add balance to generate videos.');
            } else {
              setError(status.message || 'Video generation failed. Please try again.');
            }
            setGenerationStatus(null);
          } else {
            setGenerationStatus(status.message || 'Processing...');
          }
        } catch (err) {
          console.error('Status check error:', err);
        }
      }, 10000); // Check every 10 seconds

    } catch (err) {
      console.error('Video generation error:', err);
      setError('Failed to start video generation. Please try again.');
      setIsGenerating(false);
      setGenerationStatus(null);
    }
  };

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section data-testid="process-video-section" className="py-24 md:py-32 bg-navy relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: `url('https://grainy-gradients.vercel.app/noise.svg')`
      }} />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm tracking-widest uppercase text-gold mb-4 block">
            See Our Process
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
            From Paddy to <span className="text-gold">Package</span>
          </h2>
          <div className="gold-line bg-gold mx-auto mb-6" />
          <p className="font-body text-lg text-white/70 max-w-2xl mx-auto">
            Watch our AI-generated video showcasing the complete journey of Sawariya's premium basmati rice — 
            from harvest in Haryana's fertile fields to global shipping.
          </p>
        </div>

        {/* Video Container */}
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-black/50 aspect-video rounded-none overflow-hidden border-2 border-gold/30">
            {videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  loop
                  muted
                  playsInline
                  controls
                  autoPlay
                  preload="auto"
                  poster="https://images.unsplash.com/photo-1751779058043-9bdf3480182e?w=800&q=60"
                  onEnded={() => setIsPlaying(false)}
                  onError={(e) => console.error('Video error:', e)}
                >
                  <source src={videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {/* Video Controls Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none">
                  <div className="flex items-center justify-between">
                    <span className="font-body text-sm text-white/70">AI-Generated Rice Processing Video</span>
                    <span className="font-body text-sm text-gold">Sora 2</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-8">
                {isGenerating ? (
                  <>
                    <Loader2 className="w-16 h-16 text-gold animate-spin mb-6" />
                    <p className="font-body text-white text-center mb-2">
                      {generationStatus}
                    </p>
                    <p className="font-body text-white/50 text-sm text-center">
                      AI is creating a realistic video of our rice processing journey
                    </p>
                  </>
                ) : showBalanceWarning ? (
                  <>
                    <AlertTriangle className="w-16 h-16 text-gold mb-6" />
                    <p className="font-body text-gold text-center mb-4 text-lg">
                      Video Generation Credits Depleted
                    </p>
                    <p className="font-body text-white/70 text-center mb-6 max-w-md">
                      To generate AI videos, please add balance to your Universal Key. 
                      Go to Profile → Universal Key → Add Balance.
                    </p>
                    <button
                      onClick={generateVideo}
                      data-testid="retry-video-btn"
                      className="flex items-center gap-2 border border-gold text-gold px-6 py-3 font-body text-sm tracking-widest uppercase hover:bg-gold hover:text-navy-dark transition-colors"
                    >
                      <RefreshCw size={18} />
                      Try Again
                    </button>
                  </>
                ) : error ? (
                  <>
                    <Film className="w-16 h-16 text-gold/50 mb-6" />
                    <p className="font-body text-red-400 text-center mb-4">{error}</p>
                    <button
                      onClick={generateVideo}
                      data-testid="retry-video-btn"
                      className="flex items-center gap-2 bg-gold text-navy-dark px-6 py-3 font-body text-sm tracking-widest uppercase hover:bg-gold-light transition-colors"
                    >
                      <RefreshCw size={18} />
                      Try Again
                    </button>
                  </>
                ) : (
                  <>
                    <Film className="w-16 h-16 text-gold/50 mb-6" />
                    <p className="font-body text-white/70 text-center mb-6">
                      Generate an AI-powered video showcasing our rice processing journey
                    </p>
                    <button
                      onClick={generateVideo}
                      data-testid="generate-video-btn"
                      className="flex items-center gap-2 bg-gold text-navy-dark px-8 py-4 font-body text-sm tracking-widest uppercase hover:bg-gold-light transition-colors"
                    >
                      <Play size={18} />
                      Generate Video
                    </button>
                    <p className="font-body text-white/40 text-xs mt-4 text-center">
                      Video generation takes approximately 5-10 minutes
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Video Info */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="bg-white/10 backdrop-blur-sm p-4 border border-white/20">
              <span className="font-body text-gold text-sm tracking-widest uppercase">Powered By</span>
              <p className="font-heading text-white text-lg mt-1">Sora 2 AI</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 border border-white/20">
              <span className="font-body text-gold text-sm tracking-widest uppercase">Duration</span>
              <p className="font-heading text-white text-lg mt-1">~12 Seconds</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 border border-white/20">
              <span className="font-body text-gold text-sm tracking-widest uppercase">Quality</span>
              <p className="font-heading text-white text-lg mt-1">HD 720p</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
